import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_PARTNER, USER_ROLE_ADMIN } from 'common/userRoles';

import {
  sunRiseEnabled,
  tenantScreeningEnabled,
  getPartnerSignupType,
  getSunroomUtilityPortalUrl,
} from 'common/partnerHelpers';

import { PARTNER_TYPE_AGENT } from 'common/partnerConstants';

import { PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD } from 'common/propertyAttributeConstants';

import { DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS } from 'common/dataTableConstants';

import PartnerOverviewPageSunRise from 'client/components/PartnerOverviewPageSunRise';
import PartnerOverviewPageFullService from 'client/components/PartnerOverviewPageFullService';
import PartnerOverviewPageTenantScreening from 'client/components/PartnerOverviewPageTenantScreening';
import SrPartnerPage from 'client/components/SrPartnerPage';
import SrPartnerDashboardBlankState from 'client/components/SrPartnerDashboardBlankState/SrPartnerDashboardBlankState';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';

import { SCREEN_ID_PARTNER_OVERVIEW } from 'client/constants/screenIds';

import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withLocationQuery from 'client/helpers/withLocationQuery';
import allowedRoles from 'client/helpers/allowedRoles';
import {
  dataTable,
  actionInitializeDataTableState,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionDataTableReset,
} from 'client/helpers/dataTables';

import branch from 'client/core/branch';
import PartnerActions from 'client/actions/Partner';
import ListingActions from 'client/actions/Listing';
import HelpCardActions from 'client/actions/HelpCard';
import PartnerUserActions from 'client/actions/PartnerUser';
import PropertyAttributeActions from 'client/actions/PropertyAttribute';
import PartnerTaskActions from 'client/actions/PartnerTask';
import MetabaseChartActions from 'client/actions/MetabaseChart';
import { getPropertyAttributeListKeyId } from 'client/actions/PropertyAttribute/getList';

function getMarketingListingDataTableSettings({ match }) {
  const { partnerSlug } = match.params;

  if (!partnerSlug) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'marketingPartnerListing',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `marketing-partner-slug-single-${partnerSlug}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'partners.slug': partnerSlug,
    },
  };
  return dataTableSettings;
}

@withLocationQuery
@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_OVERVIEW)
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  appUrl: ['env', 'APP_URL'],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.propertyAttributes = [
      'propertyAttributes',
      'listBy',
      getPropertyAttributeListKeyId({
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      }),
    ];
    cursors.helpCards = [
      'helpCards',
      'list',
      [SCREEN_ID_PARTNER_OVERVIEW, getPartnerSignupType(partner)].join('-'),
    ];
    cursors.partnerTasks = ['partnerTasks', 'byPartnerId', partner.id];
    cursors.metabaseChart = ['metabaseCharts', 'byPartnerId', partner.id];
  }

  return cursors;
})
@dataTable(getMarketingListingDataTableSettings)
class PartnerOverviewPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Overview | Sunroom Rentals',
  });

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const promises = lodashCompact([
      !partner &&
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
    ]);

    const dataTableSettings = getMarketingListingDataTableSettings({ match });
    actionInitializeDataTableState(inject, dataTableSettings);
    promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));

    return Promise.all(promises).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);

      if (partner) {
        return Promise.all([
          PropertyAttributeActions.getList(inject, {
            partnerId: partner.id,
            category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
          }),
          PartnerUserActions.getPartnerUserListByPartnerId(inject, {
            partnerId: partner.id,
          }),
          HelpCardActions.getList(inject, {
            screenId: SCREEN_ID_PARTNER_OVERVIEW,
            partnerSignupType: getPartnerSignupType(partner),
          }),
          PartnerTaskActions.getList(inject, {
            partnerId: partner.id,
          }),
          MetabaseChartActions.getByPartnerId(inject, {
            partnerId: partner.id,
          }),
        ]);
      }
    });
  }

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    partner: PropTypes.object,
    propertyAttributes: PropTypes.array,
    helpCards: PropTypes.array,
    marketingPartnerListing: PropTypes.object,
    partnerTasks: PropTypes.array,
    appUrl: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
  };

  constructor(...args) {
    super(...args);
    this.state = {
      shouldShowRenewListingModal: this.props.locationQuery.renewalListingId,
      shouldShowListingRenewalEmailStoppedModal: this.props.locationQuery
        .stopListingRenewalEmailListingId,
    };
  }

  componentDidMount = () => {
    const { locationQuery, dispatch } = this.props;

    if (locationQuery.renewalListingId) {
      this._onRenewalLinkClicked({
        renewalListingId: parseInt(locationQuery.renewalListingId, 10),
        dispatch,
      });
    }

    if (locationQuery.stopListingRenewalEmailListingId) {
      this._onStopListingRenewalEmailLinkClicked({
        renewalListingId: parseInt(locationQuery.stopListingRenewalEmailListingId, 10),
        dispatch,
      });
    }
  };

  _scrollToGoingToMarketSection = () => {
    const element = global.document.querySelector('.GoingToMarketSection');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', alignToTop: true });
    }
  };

  _onReloadDataTableMarketingPartnerListing = (marketingPartnerListing) => {
    const { dispatch, partner, history } = this.props;

    dispatch(actionDataTableReset, {
      dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    }).then(() => {
      this._scrollToGoingToMarketSection();
      history.replace(
        `/partner/${partner.slug}?newMarketingListingId=${marketingPartnerListing.id}`,
      );
    });
  };

  _onRenewalLinkClicked = ({ renewalListingId, dispatch }) => {
    dispatch(ListingActions.updateListingRenewalSegmentEvent, {
      originalListingId: renewalListingId,
      renewalLinkClicked: new Date().toISOString(),
    });
  };

  _onStopListingRenewalEmailLinkClicked = ({ renewalListingId, dispatch }) => {
    dispatch(ListingActions.updateListingRenewalSegmentEvent, {
      originalListingId: renewalListingId,
      stoppedEmailCampaign: new Date().toISOString(),
    });
  };

  render() {
    const {
      partner,
      propertyAttributes,
      helpCards,
      marketingPartnerListing,
      partnerTasks,
      dataTableIsFullyLoaded,
      appUrl,
      history,
      currentUser,
      metabaseChart,
      dispatch,
    } = this.props;

    const { shouldShowRenewListingModal, shouldShowListingRenewalEmailStoppedModal } = this.state;

    if (!partner) {
      return <div className="loading-gif" />;
    }

    if (partner.invalidSignup !== false) {
      const srLeasingUrl = 'https://www.sunroomleasing.com/newpartnersignup';

      if (global.location) {
        global.location.replace(srLeasingUrl);
        return null;
      }

      return <Redirect to={srLeasingUrl} />;
    }

    if (partner.type === PARTNER_TYPE_AGENT && !marketingPartnerListing && dataTableIsFullyLoaded) {
      return (
        <SrPartnerDashboardBlankState
          navbarIconUrl={partner.logoUrl}
          to={`/partner/${partner.slug}/market-property`}
          salesRep={partner.salesRep}
          currentPartner={partner}
          currentUser={currentUser}
          helpCards={helpCards}
          appUrl={appUrl}
          onGenerateShareLink={this._onGenerateShareLink}
        />
      );
    }

    let PageComponent = PartnerOverviewPageFullService;
    const pageComponentDefaultProps = {
      appUrl: appUrl,
      history: history,
      partner: partner,
      dispatch: dispatch,
      helpCards: helpCards,
      currentUser: currentUser,
      propertyAttributes: propertyAttributes,
      marketingPartnerListing: marketingPartnerListing,
      partnerTasks: partnerTasks,
      onReloadDataTableMarketingPartnerListing: this._onReloadDataTableMarketingPartnerListing,
    };
    const isSunriseEnabled = sunRiseEnabled(partner);
    const isTenantScreeningEnabled = tenantScreeningEnabled(partner);

    if (isSunriseEnabled) {
      PageComponent = PartnerOverviewPageSunRise;
    } else if (isTenantScreeningEnabled) {
      PageComponent = PartnerOverviewPageTenantScreening;
    }

    const hasMetabaseCharts = metabaseChart?.length > 0;
    const utilitiesEnabled = partner.experienceConfiguration?.utilitiesEnabled || false;

    const navbarLinks =
      !hasMetabaseCharts && !utilitiesEnabled
        ? null
        : lodashCompact([
            {
              label: 'Home',
              value: `/partner/${partner?.slug}`,
            },
            hasMetabaseCharts && {
              label: 'Reports',
              value: `/partner/${partner?.slug}/reports`,
            },
            utilitiesEnabled && {
              label: 'Utilities',
              value: getSunroomUtilityPortalUrl(partner),
              target: '_blank',
            },
          ]);

    return (
      <>
        <SrPartnerPage navbarLinks={navbarLinks} pageClassName="PartnerOverviewPage">
          <PageComponent {...pageComponentDefaultProps} />
        </SrPartnerPage>
        {shouldShowRenewListingModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Your leasing team will message you soon!"
            message={
              'Sunroom is happy to handle the renewal so you can stay busy on more important tasks. ' +
              'Before we start, we will message you!'
            }
            maxWidth="375"
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            multiButtons={[
              {
                buttonTheme: BUTTON_THEME_GREEN_ADOBE,
                buttonText: 'Got it!',
                onButtonClick: () => this.setState({ shouldShowRenewListingModal: false }),
              },
            ]}
            underline
          />
        )}
        {shouldShowListingRenewalEmailStoppedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="You've successfully unsubscribed from that email!"
            message="Come back to Sunroom when you want to list your next property!"
            maxWidth="375"
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            multiButtons={[
              {
                buttonTheme: BUTTON_THEME_GREEN_ADOBE,
                buttonText: 'Got it!',
                onButtonClick: () =>
                  this.setState({ shouldShowListingRenewalEmailStoppedModal: false }),
              },
            ]}
            underline
          />
        )}
      </>
    );
  }
}

PartnerOverviewPage.displayName = 'PartnerOverviewPage';

export default PartnerOverviewPage;
